// Reviews (docs/PHASE7-CONTRACT.md "Partner Hub"): the average, star bars, every review with its
// items, and one reply per review (the API answers 409 after the first). Pure: no DOM, no Intl.

/** @typedef {import('@popcart/core/v1').ShopReview} ShopReview */
/** @typedef {{ avg: number | null; count: number; byStars: [number, number, number, number, number] }} ReviewSummary */

export const MAX_REPLY = 500;

/** "4.6" or "—". @param {number | null | undefined} avg */
export function avgText(avg) {
  if (avg === null || avg === undefined || !Number.isFinite(avg)) return '—';
  return avg.toFixed(1);
}

/**
 * Bars from 5★ down to 1★. byStars is [1★, 2★, 3★, 4★, 5★] (the API's order); pct is of all reviews,
 * whole numbers, for the bar width.
 * @param {ReviewSummary} s
 * @returns {{ stars: number; n: number; pct: number }[]}
 */
export function starBars(s) {
  const total = s.count || s.byStars.reduce((a, b) => a + b, 0);
  return [5, 4, 3, 2, 1].map((stars) => {
    const n = s.byStars[stars - 1] ?? 0;
    return { stars, n, pct: total ? Math.round((n / total) * 100) : 0 };
  });
}

/** "1 review" / "38 reviews". @param {number} n */
export function countText(n) {
  return `${n} ${n === 1 ? 'review' : 'reviews'}`;
}

/** Five marks, `on` of them filled. @param {number} stars */
export function marks(stars) {
  const on = Math.max(0, Math.min(5, Math.round(stars)));
  return [1, 2, 3, 4, 5].map((i) => i <= on);
}

/** The accessible name for a star row. @param {number} stars */
export function starsLabel(stars) {
  return `${stars} out of 5`;
}

/** "5 Oct" in local time. @param {string} iso */
export function dayText(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${M[d.getMonth()]}`;
}

/** "Pork BBQ, Garlic rice +2 more". @param {string[]} items @param {number} [max] */
export function itemsText(items, max = 4) {
  if (!items.length) return '';
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')} +${items.length - max} more`;
}

/** Can the shop still reply? One reply per review. @param {ShopReview} r */
export function canReply(r) {
  return !r.reply;
}

/** The reply box's problem, or null. @param {string} text */
export function replyProblem(text) {
  const t = String(text ?? '').trim();
  if (!t) return 'Write a reply first.';
  if (t.length > MAX_REPLY) return `Keep it under ${MAX_REPLY} characters.`;
  return null;
}

/** "120 left". @param {string} text */
export function charsLeft(text) {
  return MAX_REPLY - String(text ?? '').length;
}

/**
 * The sentence for a failed reply. 409 = already answered (the page refetches to show it).
 * @param {{ status?: number; message?: string } | null | undefined} err
 */
export function replyError(err) {
  if (err && err.status === 409) return 'This review already has a reply.';
  if (err && err.status === 400 && err.message && !/^Request failed \(\d+\)$/.test(err.message)) return err.message;
  if (err && err.status === 0) return 'Can’t reach PopCart. Check your connection.';
  return 'Couldn’t send your reply. Try again.';
}

/**
 * Put a saved reply into the loaded pages without a refetch.
 * @template {{ page: { items: ShopReview[] } }} P
 * @param {P[]} pages
 * @param {ShopReview} updated
 * @returns {P[]}
 */
export function replaceReview(pages, updated) {
  return pages.map((p) => ({ ...p, page: { ...p.page, items: p.page.items.map((r) => (r.ratingId === updated.ratingId ? updated : r)) } }));
}

/** Waiting for a reply, across the loaded pages. @param {ShopReview[]} list */
export function unrepliedCount(list) {
  return list.filter((r) => !r.reply).length;
}

/**
 * The client calls the page makes (tests pass a fake api).
 * @param {Pick<import('@popcart/core/v1').PopCartApi, 'shopReviews' | 'replyReview'>} api
 * @param {string} shopId
 */
export function reviewActions(api, shopId) {
  return {
    /** @param {string | undefined} cursor */
    page: (cursor) => api.shopReviews(shopId, cursor),
    /** @param {string} ratingId @param {string} text */
    reply: (ratingId, text) => api.replyReview(shopId, ratingId, String(text).trim()),
  };
}
